import React, { useEffect, useRef, useState } from 'react';
import './TripSelect.css';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { Button, Col, Row, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import FormField from '../layout/FormField';
import { useAuth } from '../../hooks/useAuth';
import { WebApiCalls } from '../../webApiCalls/WebApiCalls';
import ReactSelect from 'react-select';
import { FaTrash } from 'react-icons/fa';
import decryptData from '../../helpers/Decryption';
import { selectorStyles } from '../Agencies/AddAgencies';
import _ from 'lodash';
import { toast } from 'react-toastify';
import TripSelect from './TripSelect';
import RouteSelect from './RouteSelect';

// Function to compute service_type based on calendar_data

const getServiceType = (calendarData) => {
    if (!calendarData) return 'Unknown service';

    const { monday, tuesday, wednesday, thursday, friday, saturday, sunday } = calendarData;

    if (monday === 1 && tuesday === 1 && wednesday === 1 && thursday === 1 && friday === 1) {
        return 'Weekday service';
    }
    if (saturday === 1) {
        return 'Saturday service';
    }
    if (sunday === 1) {
        return 'Sunday service';
    }
    if (monday === 0 && tuesday === 0 && wednesday === 0 && thursday === 0 && friday === 0 && saturday === 0 && sunday === 0) {
        return 'Special service';
    }
    return 'Unknown service';
};

const AddDemograph = () => {
    const { user, getToken } = useAuth();
    const navigate = useNavigate();
    const [agencies, setAgencies] = useState([]);
    const [stations, setStations] = useState([]);
    const [stopStations, setStopStations] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [trips, setTrips] = useState([]);
    const [routesLoading, setRoutesLoading] = useState(false);
    const [tripsLoading, setTripsLoading] = useState(false);
    const [transitType, setTransitType] = useState(false);
    const [agencySelection, setAgencySelection] = useState('');
    const [stopData, setStopData] = useState({
        stations: {}, // For start stations
        endStations: {}, // For end stations
        routes: {},
        trips: {}
    });
    const [startStationLoading, setStartStationLoading] = useState(false);
    const [endStationLoading, setEndStationLoading] = useState(false);
    const [tripPage, setTripPage] = useState(1);
    const [hasMoreTrips, setHasMoreTrips] = useState(true);
    const [tripSearch, setTripSearch] = useState('');
    const transitOptions = [
        { value: 'subway', label: 'Subway' },
        { value: 'train', label: 'Train' },
        { value: 'streetcar', label: 'Street Car' },
        { value: 'bus', label: 'Bus' }
    ];
    const [routePage, setRoutePage] = useState(1);
    const [hasMoreRoutes, setHasMoreRoutes] = useState(true);
    const [routeSearch, setRouteSearch] = useState('');

    useEffect(() => {
        const fetchAgencies = async () => {
            const authToken = getToken();
            if (!authToken) return;
            try {
                const res = await new WebApiCalls().getAllAgencies(authToken, 100, 0);
                if (res?.code === 200) {
                    setAgencies(res?.data?.agencies || []);
                }
            } catch (err) {
                console.error('Error fetching agencies:', err);
            }
        };
        fetchAgencies();
    }, [getToken]);

    const fetchRootStopStations = async (tripId, stationId, setFunction) => {
        const authToken = getToken();
        if (!authToken || !tripId) return;

        setEndStationLoading(true);
        try {
            const res = await new WebApiCalls().getNextStationsByTransitAgency(authToken, tripId, stationId);

            if (res?.code === 200) {
                const decryptedData = await decryptData(res.data);
                setFunction(decryptedData || []);
            }
        } catch (err) {
            console.error('Error fetching root stop stations:', err);
        } finally {
            setEndStationLoading(false);
        }
    };

    const fetchAgencyRoutes = async (agencyId, agencyType, setFunction, page = 1, append = false) => {
        setRoutesLoading(true);
        const authToken = getToken();
        if (!authToken || !agencyId) return;

        try {
            // Ensure agencyType is an array of values
            const agencyTypeValues = agencyType.map((type) => type.value);

            if (agencyTypeValues?.length === 0) return;

            const res = await new WebApiCalls().getRoutesByAgency(authToken, agencyId, agencyTypeValues, page, 20);
            if (res?.code === 200) {
                const decryptedData = await decryptData(res.data);
                setFunction((prev) => {
                    if (!append) return decryptedData || [];
                    // Filter out duplicates by _id
                    const existingIds = new Set(prev.map((r) => r._id));
                    const filtered = (decryptedData || []).filter((r) => !existingIds.has(r._id));
                    return [...prev, ...filtered];
                });
                setHasMoreRoutes((decryptedData?.length || 0) === 20);
            }
        } catch (err) {
            console.error('Error fetching routes:', err);
        } finally {
            setRoutesLoading(false);
        }
    };

    const searchDebounce = useRef(
        _.debounce((routeId, search, setFunction) => {
            fetchRouteTrips(routeId, search, setFunction);
        }, 300)
    ).current;

    const routeSearchDebounce = useRef(
        _.debounce((agencyId, search, setFunction) => {
            setRoutePage(1);
            fetchAgencyRoutes(agencyId, transitType, setFunction, 1, false);
        }, 300)
    ).current;

    // Add abort controller for API calls
    const abortControllerRef = useRef(new AbortController());

    // Update fetchRouteTrips to use pagination and append results
    const fetchRouteTrips = async (routeId, search = '', setFunction, page = 1, append = false) => {
        setTripsLoading(true);
        if (!append) setFunction([]);
        const authToken = getToken();
        if (!authToken || !routeId) return;
        try {
            // Cancel previous request
            abortControllerRef.current.abort();
            abortControllerRef.current = new AbortController();
            const res = await new WebApiCalls().getRouteTrips(authToken, routeId, search, page, 10);
            if (res?.code === 200) {
                const decryptedData = await decryptData(res.data);
                const newTrips = decryptedData?.trips || [];
                setFunction((prev) => {
                    if (!append) return newTrips;
                    // Filter out duplicates by _id
                    const existingIds = new Set(prev.map((t) => t._id));
                    const filtered = newTrips.filter((t) => !existingIds.has(t._id));
                    return [...prev, ...filtered];
                });
                setHasMoreTrips(newTrips.length === 10); // If less than 10, no more pages
            } else {
                setHasMoreTrips(false);
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('Error fetching trips:', err);
            }
        } finally {
            setTripsLoading(false);
        }
    };

    // Handler for infinite scroll in TripSelect
    const handleTripMenuScrollToBottom = (routeId, setFunction) => {
        if (hasMoreTrips) {
            const nextPage = tripPage + 1;
            setTripPage(nextPage);
            fetchRouteTrips(routeId, tripSearch, setFunction, nextPage, true);
        }
    };

    // Handler for search in TripSelect
    const handleTripInputChange = (routeId, search, setFunction) => {
        setTripSearch(search);
        setTripPage(1);
        fetchRouteTrips(routeId, search, setFunction, 1, false);
    };

    // Handler for infinite scroll in RouteSelect
    const handleRouteMenuScrollToBottom = (agencyId, setFunction) => {
        if (hasMoreRoutes) {
            setRoutePage((prev) => {
                const nextPage = prev + 1;
                fetchAgencyRoutes(agencyId, transitType, setFunction, nextPage, true);
                return nextPage;
            });
        }
    };

    // Handler for search in RouteSelect
    const handleRouteInputChange = (agencyId, search, setFunction) => {
        setRouteSearch(search);
        setRoutePage(1);
        fetchAgencyRoutes(agencyId, transitType, setFunction, 1, false);
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        const authToken = getToken();
        if (!authToken) return;

        const requestData = {
            title: values.title,
            transit_agencies: [values.agency_id, ...values.stops.map((stop) => stop.stop_agency_id)].filter(Boolean),
            start_route_id: values.start_route_id,
            start_trip_id: values.start_trip_id,
            transit_types: values.transit_types?.map((type) => type.value) || [],
            start_station: values.start_station_id,
            end_station: values.end_station_id,
            is_default: values.is_default || false,
            additional_stations: values.stops.map((stop) => ({
                stop_agency_id: stop.stop_agency_id,
                transit_types: stop.transit_types.map((type) => type.value),
                route_id: stop.route_id,
                trip_id: stop.trip_id,
                start_station_id: stop.start_station_id,
                end_station_id: stop.end_station_id,
                is_destination: stop.isDestination
            }))
        };

        try {
            const response = await new WebApiCalls().saveCommuterLocation(authToken, user?._id, requestData);
            if (response?.code === 200) {
                navigate('/frequently-used-locations');
            }

            if (response?.code === 400) {
                toast.error(response?.desc);
                return;
            }
        } catch (error) {
            console.error('API Error:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const fetchStopStations = async (tripId, stopIndex, stationId, dataRef) => {
        const authToken = getToken();
        if (!authToken || !tripId) return;

        try {
            const res = await new WebApiCalls().getNextStationsByTransitAgency(authToken, tripId, stationId);

            if (res?.code === 200) {
                const decryptedData = await decryptData(res.data);
                setStopData((prev) => ({
                    ...prev,
                    [dataRef]: {
                        // Use dataRef dynamically
                        ...prev[dataRef],
                        [stopIndex]: decryptedData || []
                    }
                }));
            }
        } catch (err) {
            console.error('Error fetching stations:', err);
        }
    };

    const fetchStopRoutes = async (agencyId, transitTypes, stopIndex) => {
        const authToken = getToken();
        if (!authToken || !agencyId || !transitTypes.length) return;

        try {
            const agencyTypeValues = transitTypes.map((type) => type.value);
            const res = await new WebApiCalls().getRoutesByAgency(authToken, agencyId, agencyTypeValues);

            if (res?.code === 200) {
                const decryptedData = await decryptData(res.data);
                setStopData((prev) => ({
                    ...prev,
                    routes: { ...prev.routes, [stopIndex]: decryptedData || [] }
                }));
            }
        } catch (err) {
            console.error('Error fetching routes:', err);
        }
    };

    const fetchStopTrips = async (routeId, stopIndex) => {
        const authToken = getToken();
        if (!authToken || !routeId) return;

        try {
            const res = await new WebApiCalls().getRouteTrips(authToken, routeId);
            if (res?.code === 200) {
                const decryptedData = await decryptData(res.data);
                setStopData((prev) => ({
                    ...prev,
                    trips: {
                        ...prev.trips,
                        [stopIndex]: decryptedData?.trips || []
                    }
                }));
            }
        } catch (err) {
            console.error('Error fetching trips:', err);
        }
    };

    // Custom formatOptionLabel to show service_type as a tag
    const formatOptionLabel = ({ label, service_type }) => (
        <div className="trip-option" title={label}>
            {' '}
            {/* Add title for tooltip on hover */}
            <span>{label}</span>
            {service_type && <span className={`service-tag service-tag-${service_type.toLowerCase().replace(' ', '-')}`}>{service_type}</span>}
        </div>
    );

    return (
        <Row className="d-flex justify-content-center">
            <Col xs={12} sm={12} md={11} lg={11} className="shadow-lg p-5 rounded m-3">
                <div className="p-5 form_container mx-auto">
                    <Formik
                        initialValues={{
                            title: '',
                            agency_id: '',
                            start_route_id: '',
                            start_trip_id: '',
                            start_station_id: '',
                            end_station_id: '',
                            stops: []
                        }}
                        validationSchema={Yup.object({
                            title: Yup.string().required('Required'),
                            agency_id: Yup.string().required('Required'),
                            start_station_id: Yup.string().required('Required'),
                            end_station_id: Yup.string().required('Required')
                        })}
                        onSubmit={handleSubmit}
                    >
                        {({ values, setFieldValue }) => (
                            <FormikForm>
                                <Row>
                                    <Col md={6} className="mb-3">
                                        <div className="mt-2">
                                            <FormField label="Title" name="title" type="text" />
                                        </div>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <div className="mb-3">
                                            <Form.Label>Select Agency</Form.Label>
                                            <ReactSelect
                                                options={agencies.map((agency) => ({ label: agency.name, value: agency._id }))}
                                                onChange={(selectedOption) => {
                                                    setFieldValue('agency_id', selectedOption.value);
                                                    setFieldValue('start_route_id', '');

                                                    setAgencySelection(selectedOption.value);
                                                    setRoutes([]);
                                                }}
                                                placeholder="Select an agency"
                                            />
                                        </div>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <div className="mb-3">
                                            <Form.Group>
                                                <Form.Label className="form-label mb-2">Transit Type</Form.Label>
                                                <ReactSelect
                                                    options={
                                                        values.agency_id
                                                            ? transitOptions.filter((option) => {
                                                                  const agency = agencies.find((agency) => agency._id === values.agency_id);
                                                                  return agency?.transit_mediums?.includes(option.value);
                                                              })
                                                            : []
                                                    }
                                                    isMulti
                                                    customStyles={selectorStyles}
                                                    value={transitType}
                                                    onChange={(e) => {
                                                        setTransitType(e);
                                                        fetchAgencyRoutes(values.agency_id, e, setRoutes);
                                                    }}
                                                    placeholder="Select transit type"
                                                />
                                            </Form.Group>
                                        </div>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <RouteSelect
                                            routes={routes}
                                            routesLoading={routesLoading}
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            setRoutes={setRoutes}
                                            searchDebounce={routeSearchDebounce}
                                            fetchAgencyRoutes={fetchAgencyRoutes}
                                            onRouteMenuScrollToBottom={handleRouteMenuScrollToBottom}
                                            onRouteInputChange={handleRouteInputChange}
                                        />
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <TripSelect
                                            trips={trips}
                                            tripsLoading={tripsLoading}
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            setTrips={setTrips}
                                            setStations={setStations}
                                            searchDebounce={searchDebounce}
                                            fetchRouteTrips={fetchRouteTrips}
                                            fetchRootStopStations={fetchRootStopStations}
                                            onTripMenuScrollToBottom={handleTripMenuScrollToBottom}
                                            onTripInputChange={handleTripInputChange}
                                        />
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        {/* Start Station */}
                                        <div className="mb-3">
                                            <Form.Label>Start Station</Form.Label>
                                            <ReactSelect
                                                options={stations?.map((station) => {
                                                    // Check if stop_code is included in stop_name
                                                    const isCodeInName = station.stop_name.includes(station.stop_code);
                                                    // Construct label: use only stop_name if stop_code is included, otherwise combine
                                                    const label = isCodeInName ? station.stop_name : `${station.stop_name} - ${station.stop_code}`;
                                                    // Remove "#" from the label
                                                    const finalLabel = label.replace(/#/g, '');

                                                    return {
                                                        label: finalLabel,
                                                        value: station._id
                                                    };
                                                })}
                                                isLoading={startStationLoading}
                                                onChange={(selectedOption) => {
                                                    setFieldValue('start_station_id', selectedOption.value);
                                                    setStartStationLoading(true);
                                                    fetchAgencyRoutes(values.agency_id, transitType, setRoutes).finally(() => {
                                                        setStartStationLoading(false);
                                                    });

                                                    // Reset end station selection
                                                    setFieldValue('end_station_id', '');
                                                    setStopStations([]);

                                                    if (values?.start_trip_id) {
                                                        fetchRootStopStations(values?.start_trip_id, selectedOption.value, setStopStations);
                                                    }
                                                }}
                                                placeholder="Select start station"
                                            />
                                        </div>
                                    </Col>

                                    <Col md={6} className="mb-3">
                                        {/* End Station */}
                                        <div className="mb-3">
                                            <Form.Label>End Station (Stop 1)</Form.Label>
                                            <ReactSelect
                                                options={stopStations?.map((station) => {
                                                    // Check if stop_code is included in stop_name
                                                    const isCodeInName = station.stop_name.includes(station.stop_code);
                                                    // Construct label: use only stop_name if stop_code is included, otherwise combine
                                                    const label = isCodeInName ? station.stop_name : `${station.stop_name} - ${station.stop_code}`;
                                                    // Remove "#" from the label
                                                    const finalLabel = label.replace(/#/g, '');

                                                    return {
                                                        label: finalLabel,
                                                        value: station._id
                                                    };
                                                })}
                                                isLoading={endStationLoading}
                                                onChange={(selectedOption) => {
                                                    setFieldValue('end_station_id', selectedOption.value);
                                                }}
                                                placeholder="Select end station"
                                            />
                                        </div>
                                    </Col>

                                    {values?.stops.map((stop, index) => (
                                        <div key={index} className="mb-3">
                                            <h5 className="text-center my-3">Stop {index + 2}</h5>
                                            <Row>
                                                <Col md={6} className="mb-3">
                                                    {/* Agency Selection */}
                                                    <div className="mb-3">
                                                        <Form.Label>Select Stop Agency</Form.Label>
                                                        <ReactSelect
                                                            options={agencies.map((agency) => ({
                                                                label: agency.name,
                                                                value: agency._id
                                                            }))}
                                                            onChange={(selectedOption) => {
                                                                const updatedStops = values.stops.map((s, i) =>
                                                                    i === index
                                                                        ? {
                                                                              ...s,
                                                                              stop_agency_id: selectedOption.value,
                                                                              transit_types: [], // Reset transit types
                                                                              route_id: '', // Reset dependent fields
                                                                              trip_id: ''
                                                                          }
                                                                        : s
                                                                );
                                                                setFieldValue('stops', updatedStops);
                                                            }}
                                                            placeholder="Select stop agency"
                                                        />
                                                    </div>
                                                </Col>
                                                <Col md={6} className="mb-3">
                                                    {/* Transit Type Selection */}
                                                    <div className="mb-3">
                                                        <Form.Label>Transit Type</Form.Label>
                                                        <ReactSelect
                                                            options={
                                                                stop.stop_agency_id
                                                                    ? transitOptions.filter((option) => {
                                                                          const agency = agencies.find((a) => a._id === stop.stop_agency_id);
                                                                          return agency?.transit_mediums?.includes(option.value);
                                                                      })
                                                                    : []
                                                            }
                                                            isMulti
                                                            styles={selectorStyles}
                                                            value={stop.transit_types}
                                                            onChange={(selectedOptions) => {
                                                                const updatedStops = values.stops.map((s, i) =>
                                                                    i === index ? { ...s, transit_types: selectedOptions } : s
                                                                );
                                                                setFieldValue('stops', updatedStops);
                                                                // Pass transit type VALUES (not objects) to the route fetcher
                                                                fetchStopRoutes(
                                                                    stop.stop_agency_id,
                                                                    selectedOptions.map((opt) => opt.value),
                                                                    index
                                                                );
                                                            }}
                                                            placeholder="Select transit type"
                                                        />
                                                    </div>
                                                </Col>
                                                <Col md={6} className="mb-3">
                                                    {/* Route Selection */}
                                                    <div className="mb-3">
                                                        <Form.Label>Route</Form.Label>
                                                        <ReactSelect
                                                            options={
                                                                stopData.routes[index]?.map((route) => ({
                                                                    label: `${route.route_short_name || ''} - ${route.route_long_name || ''}`,
                                                                    value: route._id
                                                                })) || []
                                                            }
                                                            onChange={(selectedOption) => {
                                                                const updatedStops = values.stops.map((s, i) =>
                                                                    i === index ? { ...s, route_id: selectedOption.value } : s
                                                                );
                                                                setFieldValue('stops', updatedStops);
                                                                fetchStopTrips(selectedOption.value, index);
                                                            }}
                                                            placeholder="Select route"
                                                        />
                                                    </div>
                                                </Col>
                                                <Col md={6} className="mb-3">
                                                    {/* Trip Selection */}
                                                    <div className="mb-3">
                                                        <Form.Label>Trip</Form.Label>
                                                        <ReactSelect
                                                            options={
                                                                stopData.trips[index]?.map((trip) => ({
                                                                    label: `${trip.name}`,
                                                                    value: trip._id,
                                                                    service_type: getServiceType(trip.calendar_data)
                                                                })) || []
                                                            }
                                                            onChange={(selectedOption) => {
                                                                const updatedStops = values.stops.map((s, i) =>
                                                                    i === index ? { ...s, trip_id: selectedOption.value } : s
                                                                );
                                                                setFieldValue('stops', updatedStops);

                                                                // Fetch stations for this trip
                                                                fetchStopStations(selectedOption.value, index, '', 'stations');
                                                            }}
                                                            placeholder="Select trip"
                                                            formatOptionLabel={formatOptionLabel}
                                                            classNamePrefix="react-select"
                                                        />
                                                    </div>
                                                </Col>
                                                <Col md={6} className="mb-3">
                                                    {/* Start Station for Stop */}
                                                    <div className="mb-3">
                                                        <Form.Label>Start Station</Form.Label>
                                                        <ReactSelect
                                                            options={
                                                                stopData.stations[index]?.map((station) => {
                                                                    // Check if stop_code is included in stop_name
                                                                    const isCodeInName = station.stop_name.includes(station.stop_code);
                                                                    // Construct label: use only stop_name if stop_code is included, otherwise combine
                                                                    const label = isCodeInName ? station.stop_name : `${station.stop_name} - ${station.stop_code}`;
                                                                    // Remove "#" from the label
                                                                    const finalLabel = label.replace(/#/g, '');

                                                                    return {
                                                                        label: finalLabel,
                                                                        value: station._id
                                                                    };
                                                                }) || []
                                                            }
                                                            isLoading={stop.startStationLoading}
                                                            onChange={(selectedOption) => {
                                                                const updatedStops = values.stops.map((s, i) =>
                                                                    i === index
                                                                        ? {
                                                                              ...s,
                                                                              start_station_id: selectedOption.value,
                                                                              startStationLoading: true
                                                                          }
                                                                        : s
                                                                );
                                                                setFieldValue('stops', updatedStops);

                                                                // Fetch stations for this trip and the selected start station
                                                                fetchStopStations(values.stops[index].trip_id, index, selectedOption.value, 'endStations').finally(() => {
                                                                    const updatedStops = values.stops.map((s, i) =>
                                                                        i === index
                                                                            ? {
                                                                                  ...s,
                                                                                  startStationLoading: false // Reset loading state
                                                                              }
                                                                            : s
                                                                    );
                                                                    setFieldValue('stops', updatedStops);
                                                                });
                                                            }}
                                                            placeholder="Select start station"
                                                        />
                                                    </div>
                                                </Col>
                                                <Col md={6} className="mb-3">
                                                    {/* End Station for Stop */}
                                                    <div className="mb-3">
                                                        <Form.Label>End Station</Form.Label>
                                                        <ReactSelect
                                                            options={
                                                                stopData.endStations[index]?.map((station) => {
                                                                    // Check if stop_code is included in stop_name
                                                                    const isCodeInName = station.stop_name.includes(station.stop_code);
                                                                    // Construct label: use only stop_name if stop_code is included, otherwise combine
                                                                    const label = isCodeInName ? station.stop_name : `${station.stop_name} - ${station.stop_code}`;
                                                                    // Remove "#" from the label
                                                                    const finalLabel = label.replace(/#/g, '');

                                                                    return {
                                                                        label: finalLabel,
                                                                        value: station._id
                                                                    };
                                                                }) || []
                                                            }
                                                            isLoading={stop.endStationLoading}
                                                            onChange={(selectedOption) => {
                                                                const updatedStops = values.stops.map((s, i) =>
                                                                    i === index
                                                                        ? {
                                                                              ...s,
                                                                              end_station_id: selectedOption.value,
                                                                              endStationLoading: true
                                                                          }
                                                                        : s
                                                                );
                                                                setFieldValue('stops', updatedStops);

                                                                // Simulate an API call or other logic if needed
                                                                setTimeout(() => {
                                                                    const updatedStops = values.stops.map((s, i) =>
                                                                        i === index
                                                                            ? {
                                                                                  ...s,
                                                                                  endStationLoading: false // Reset loading state
                                                                              }
                                                                            : s
                                                                    );
                                                                    setFieldValue('stops', updatedStops);
                                                                }, 500); // Example delay
                                                            }}
                                                            placeholder="Select end station"
                                                        />
                                                    </div>
                                                </Col>
                                            </Row>

                                            {/* Destination Checkbox */}
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <input
                                                        type="checkbox"
                                                        checked={stop.isDestination || false}
                                                        onChange={(e) => {
                                                            const updatedStops = values.stops.map((s, i) => ({
                                                                ...s,
                                                                isDestination: i === index ? e.target.checked : false
                                                            }));
                                                            setFieldValue('stops', updatedStops);
                                                        }}
                                                    />
                                                    <label className="ms-2">Mark this stop as your destination</label>
                                                </div>

                                                {/* Remove Stop Button */}
                                                <FaTrash
                                                    className="text-danger cursor-pointer"
                                                    onClick={() =>
                                                        setFieldValue(
                                                            'stops',
                                                            values.stops.filter((_, i) => i !== index)
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </Row>

                                <div className="d-flex flex-column justify-content-between align-items-end mt-4">
                                    <Button
                                        className="theme-outline-cancel-btn w-auto me-2 mt-2"
                                        onClick={() =>
                                            setFieldValue('stops', [
                                                ...values.stops,
                                                {
                                                    stop_agency_id: '',
                                                    transit_types: [],
                                                    route_id: '',
                                                    trip_id: '',
                                                    start_station_id: '',
                                                    end_station_id: '',
                                                    isDestination: false
                                                }
                                            ])
                                        }
                                        disabled={values.stops.some((stop) => stop.isDestination)}
                                    >
                                        Add Station
                                    </Button>
                                    <Button type="submit" className="theme-btn me-2 mt-2" disabled={!values.start_station_id}>
                                        Save
                                    </Button>
                                </div>
                            </FormikForm>
                        )}
                    </Formik>
                </div>
            </Col>
        </Row>
    );
};

export default AddDemograph;
